/**
 * @file CompoundPricing.js
 * @version 1.23.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-10
 * @modified 2026-10-29
 * @description Contract Pricing for Compounds
 */

import React, { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../config';
import { setContractsFulfilled, fulfilledAction } from '../utils/contractFulfilment';
import { isPastPeriodName, periodSortKey } from '../utils/periodHelper';
import { contractCoversToday } from '../utils/pricingHelpers';
import { makeContractLive, makeLiveAction } from '../utils/contractDraft';
import { formatQuantity } from '../utils/numberFormatters';
import './SearchLayout.css';
import './ContractPricing.css';
import './ResizableColumns.css';
import Dropdown from './Dropdown';
import Input from './Input';
import MultiSelectDropdown from './MultiSelectDropdown';
import PaginationControls from './PaginationControls';
import ContractPricingModal from './ContractPricingModal';
import ContactModal from './ContactModal';
import ReactDOM from 'react-dom';
import { FulfilmentBadge, LinkedPosBadge, ContractCustomerCell, ProductCell, PriceMarginCell, ContractGroupBadge } from './PricingBadges';
import RowActionsMenu from './RowActionsMenu';
import { openContractPdf } from '../utils/contractPdf';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import RecalculateQuantitiesButton from './RecalculateQuantitiesButton';
import RefreshAutoLinksButton from './RefreshAutoLinksButton';
import ContractOrdersModal from './ContractOrdersModal';
import { formatPrice } from '../utils/currencyFormatter';
import { useSlideOutButton } from './SlideOutButton';
import { useDragSelect } from '../hooks/useDragSelect';
import usePersistedState from '../hooks/usePersistedState';
import useResizableColumns from '../hooks/useResizableColumns';
import useTableContextMenu from '../hooks/useTableContextMenu';
import TableContextMenu from './TableContextMenu';
import { matchesAllTerms, searchFieldsSummary, buildGroupHaystacks } from '../utils/searchMatch';
import SearchHelp from './SearchHelp';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';

// Columns the free-text search box looks at; narrowable via the field-selector
// dropdown beside the search input.
const SEARCH_FIELDS = [
  { key: 'customer', label: 'Customer', get: c => c.company_name },
  { key: 'account', label: 'Account', get: c => c.account_no },
  // The product group a contract was made from (29 Oct 2026) is found as a product.
  { key: 'product', label: 'Product', get: c => [c.product_name, c.product_group && c.product_group.name].filter(Boolean).join(' ') },
  { key: 'code', label: 'Product Code', get: c => c.product_code },
  /*
   * Both kinds of number, because the Contract # column shows both: our own
   * (grey chips, Excel Agri ↔ customer) above the supplier's (tinted chip).
   * Searching only contract_number meant a supplier reference could be sitting
   * in plain sight on the row and still not be findable — on the live blends
   * data 146 of 239 contracts carry a "PR-" supplier number and none carries
   * one in contract_number, so searching "PR-" returned a single row, and that
   * one only because someone had typed the reference into the product name.
   */
  { key: 'contract', label: 'Contract #', get: c => [c.contract_number, c.supplier_contract_number].filter(Boolean).join(' ') },
];
const ALL_SEARCH_KEYS = SEARCH_FIELDS.map(f => f.key);
const SEARCH_FIELD_OPTIONS = SEARCH_FIELDS.map(f => ({ value: f.key, label: f.label }));

/*
 * What Clear Filters resets to — and so what "no filters applied" means for
 * the button's highlight (utils/activeFilters.js).
 */
const CLEARED_FILTERS = {
  period: '', search: '', rep: '', mill: '',
  limit: '50', showArchived: false, showContracted: true
};
const FILTER_LABELS = { showArchived: 'Archived', showContracted: 'Contracted' };

function CompoundPricing({ user }) {
  const [contracts, setContracts] = useState([]);
  const [periods, setPeriods] = useState([]);
  // "Add orders…" on a row: { set, title } (ContractOrdersModal, 28 Oct 2026).
  const [ordersFor, setOrdersFor] = useState(null);
  const [reps, setReps] = useState([]);
  const [mills, setMills] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedContactId, setSelectedContactId] = usePersistedState('compoundPricing.contactModalId', null);
  const [modalMode, setModalMode] = useState('edit');
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });
  const [notesDialog, setNotesDialog] = useState({ isOpen: false, notes: '', title: '' });
  const [selectedIds, setSelectedIds] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Drag-to-resize columns (13 columns: checkbox, expand, customer, product,
  // contract #, period, dates, supplier, quant, price, margin, rep, actions).
  const { tableRef, ResizeHandle, ColGroup, ResetButton } = useResizableColumns('compoundPricing', 12);

  // Slideout buttons — after selectedIds declared so bulk action buttons get sized
  useSlideOutButton([selectedIds.length]);
  const [filters, setFilters] = usePersistedState('compoundPricing.filters', {
    period: '',
    search: '',
    rep: '',
    mill: '',
    limit: '50',
    showArchived: false,
    // CONTRACTED toggle — on by default. When OFF, rows / groups that have
    // any contract number (ours or the supplier's) drop out of view.
    showContracted: true
  });
  const [searchFields, setSearchFields] = usePersistedState('compoundPricing.searchFields', ALL_SEARCH_KEYS);
  const [sorting, setSorting] = usePersistedState('compoundPricing.sorting', {
    field: '',
    direction: 'asc'
  });
  const [pagination, setPagination] = usePersistedState('compoundPricing.pagination', {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  const CATEGORY_ID = 3; // Compounds

  // Today as YYYY-MM-DD (local) for safe lexical comparison vs period date strings.
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const isCurrentPeriodName = (name) => {
    const p = periods.find(x => x.name === name);
    if (!p || !p.start_date || !p.end_date) return false;
    const start = (typeof p.start_date === 'string' ? p.start_date : new Date(p.start_date).toISOString()).slice(0, 10);
    const end = (typeof p.end_date === 'string' ? p.end_date : new Date(p.end_date).toISOString()).slice(0, 10);
    return todayStr >= start && todayStr <= end;
  };

  useEffect(() => {
    loadPeriods();
    loadReps();
    loadMills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delayTimer = setTimeout(() => {
      loadContracts();
    }, 300);
    return () => clearTimeout(delayTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.period, filters.limit]);

  const loadPeriods = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pricing/periods?category_id=${CATEGORY_ID}&is_active=all`, {
        credentials: 'include'
      });
      const data = await response.json();
      setPeriods(data);

      // Set default period to current period (Winter/Summer based on date)
      // Default is 'All Periods' — do not pre-select the current period.
    } catch (error) {
      console.error('Error loading periods:', error);
    }
  };

  const loadReps = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pricing/distinct/reps?category_id=${CATEGORY_ID}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setReps(data);
    } catch (error) {
      console.error('Error loading reps:', error);
    }
  };

  const loadMills = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pricing/distinct/suppliers?category_id=${CATEGORY_ID}`, {
        credentials: 'include'
      });
      const suppliers = await response.json();
      setMills(suppliers); // Keep using mills state for now to avoid changing all references
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const loadContracts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        category_id: CATEGORY_ID,
        limit: 10000 // Set high limit for client-side filtering/pagination
      });

      // Only add period_id if a specific period is selected
      if (filters.period && filters.period !== '') {
        params.append('period_id', filters.period);
      }

      const response = await fetch(`${API_URL}/api/pricing/contracts?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();

      const allContracts = data.data || [];
      setContracts(allContracts);

      // Reset to page 1 when data changes
      const limit = parseInt(filters.limit) || 50;
      setPagination(prev => ({
        ...prev,
        page: 1,
        limit: limit,
        total: allContracts.length,
        totalPages: Math.ceil(allContracts.length / limit)
      }));
    } catch (error) {
      console.error('Error loading contracts:', error);
      setContracts([]);
      setPagination(prev => ({ ...prev, page: 1, total: 0, totalPages: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSort = (field) => {
    setSorting(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Pre-compute the set of contract_group_ids where at least one member
  // carries a contract number — used by the CONTRACTED toggle to drop the
  // whole group when any single product has a reference attached.
  const contractedGroupIds = useMemo(() => {
    const set = new Set();
    for (const c of contracts) {
      const has = (c.contract_number && String(c.contract_number).trim())
               || (c.supplier_contract_number && String(c.supplier_contract_number).trim());
      if (has && c.contract_group_id) set.add(c.contract_group_id);
    }
    return set;
  }, [contracts]);

  const activeSearchFields = SEARCH_FIELDS.filter(f => searchFields.includes(f.key));
  // Only built while something is actually being searched for.
  const groupHaystacks = filters.search ? buildGroupHaystacks(contracts, activeSearchFields) : null;

  // Filter, sort, and paginate contracts
  const filteredAndSortedContracts = contracts
    .filter(contract => {
      // Text search filter — multi-term + "quoted phrase" over selected fields.
      // A row in a group is matched on the whole group's values, so the group
      // survives intact when only one member carries the term. See
      // buildGroupHaystacks.
      if (filters.search) {
        const haystack = (contract.contract_group_id && groupHaystacks)
          ? (groupHaystacks.get(contract.contract_group_id) || [])
          : activeSearchFields.map(f => f.get(contract));
        if (!matchesAllTerms(haystack, filters.search)) return false;
      }

      // Rep filter
      if (filters.rep && contract.rep_name !== filters.rep) {
        return false;
      }

      // Supplier filter
      if (filters.mill && contract.supplier_contact_id?.toString() !== filters.mill) {
        return false;
      }

      // Archived filter — hide archived rows unless toggle is on
      if (!filters.showArchived && contract.is_archived) {
        return false;
      }

      // CONTRACTED filter — hide rows / groups with any contract number.
      if (!filters.showContracted) {
        if (contract.contract_group_id) {
          if (contractedGroupIds.has(contract.contract_group_id)) return false;
        } else {
          const has = (contract.contract_number && String(contract.contract_number).trim())
                   || (contract.supplier_contract_number && String(contract.supplier_contract_number).trim());
          if (has) return false;
        }
      }

      return true;
    })
    .sort((a, b) => {
      if (!sorting.field) return 0;

      let aVal = a[sorting.field];
      let bVal = b[sorting.field];

      // Handle numeric fields
      if (['quantity', 'cost', 'price', 'margin'].includes(sorting.field)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sorting.field === 'period_names') {
        // Periods sort by date, not by spelling — alphabetically "Summer 2026"
        // would come before "Winter 2025-26".
        aVal = periodSortKey(periods, aVal);
        bVal = periodSortKey(periods, bVal);
      } else {
        // Handle string fields (case-insensitive)
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return sorting.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sorting.direction === 'asc' ? 1 : -1;
      return 0;
    });

  // Update pagination when filters or sorting changes
  useEffect(() => {
    const limit = parseInt(filters.limit) || 50;
    setPagination(prev => ({
      ...prev,
      page: 1,
      limit: limit,
      total: filteredAndSortedContracts.length,
      totalPages: Math.ceil(filteredAndSortedContracts.length / limit)
    }));
  }, [filteredAndSortedContracts.length, filters.search, filters.rep, filters.mill, filters.limit, sorting.field, sorting.direction]);

  // Paginate the filtered and sorted results
  const paginatedContracts = filteredAndSortedContracts.slice(
    (pagination.page - 1) * pagination.limit,
    pagination.page * pagination.limit
  );

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleAddNew = () => {
    setSelectedContract(null);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleClearFilters = () => {
    setFilters(prev => ({ ...CLEARED_FILTERS, limit: prev.limit || '50' }));
    setSearchFields(ALL_SEARCH_KEYS);
    setSorting({ field: '', direction: 'asc' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Any header filter on lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(filters, CLEARED_FILTERS, { labels: FILTER_LABELS });
  const filtersApplied = headerFilters.count > 0;

  const handleView = (contract) => {
    setSelectedContract(contract);
    setModalMode('view');
    setShowModal(true);
  };

  const handleEdit = (contract) => {
    setSelectedContract(contract);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleArchive = async (contract) => {
    const archiving = !contract.is_archived;
    try {
      const response = await fetch(`${API_URL}/api/pricing/contracts/${contract.id}/archive`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_archived: archiving })
      });
      if (response.ok) {
        loadContracts();
      } else {
        setAlertDialog({ isOpen: true, message: `Failed to ${archiving ? 'archive' : 'unarchive'} pricing entry`, type: 'error' });
      }
    } catch (error) {
      console.error('Error archiving pricing:', error);
      setAlertDialog({ isOpen: true, message: `Error ${archiving ? 'archiving' : 'unarchiving'} pricing entry`, type: 'error' });
    }
  };

  // Patch only the rows that were just edited / created instead of
  // reloading the whole list. Falls back to a full reload if the modal
  // didn't give us an id.
  const refreshRows = async ({ contractId, contractGroupId } = {}) => {
    if (!contractId && !contractGroupId) return loadContracts();
    try {
      const params = new URLSearchParams();
      if (contractGroupId) params.append('contract_group_id', contractGroupId);
      else params.append('id', contractId);
      const res = await fetch(`${API_URL}/api/pricing/contracts?${params}`, { credentials: 'include' });
      const data = await res.json();
      const refreshed = data.data || [];
      setContracts(prev => {
        const byId = new Map(refreshed.map(c => [c.id, c]));
        const updated = prev.map(c => byId.has(c.id) ? byId.get(c.id) : c);
        const existingIds = new Set(prev.map(c => c.id));
        const newRows = refreshed.filter(c => !existingIds.has(c.id));
        return [...updated, ...newRows];
      });
    } catch (err) {
      console.error('Error refreshing rows:', err);
      await loadContracts();
    }
  };

  const handleSave = async (savedInfo = {}) => {
    setShowModal(false);
    await loadPeriods();
    await refreshRows(savedInfo);
  };

  const handleToggleHold = async (contract) => {
    try {
      await fetch(`${API_URL}/api/pricing/contracts/${contract.id}/hold`, {
        method: 'PATCH',
        credentials: 'include'
      });
      loadContracts();
    } catch (error) {
      console.error('Error toggling hold:', error);
    }
  };

  const handleBulkHold = async (onHold) => {
    if (selectedIds.length === 0) return;
    try {
      await fetch(`${API_URL}/api/pricing/contracts/bulk-hold`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids: selectedIds, on_hold: onHold })
      });
      setSelectedIds([]);
      loadContracts();
    } catch (error) {
      console.error('Error bulk toggling hold:', error);
    }
  };

  const handleRefreshCost = async (contract) => {
    try {
      const res = await fetch(`${API_URL}/api/pricing/contracts/${contract.id}/refresh-cost`, {
        method: 'POST', credentials: 'include'
      });
      const data = await res.json();
      if (data.skipped) {
        setAlertDialog({ isOpen: true, message: data.reason || 'No purchase_unit_price on inventory item', type: 'warning' });
        return;
      }
      await loadContracts();
      setAlertDialog({ isOpen: true, message: `Cost refreshed to ${formatPrice(data.newCost ?? 0)}`, type: 'success' });
    } catch (error) {
      console.error('Error refreshing cost:', error);
      setAlertDialog({ isOpen: true, message: 'Failed to refresh cost', type: 'error' });
    }
  };

  const handleBulkRefreshCost = () => {
    if (selectedIds.length === 0) return;
    setConfirmDialog({
      isOpen: true,
      message: `Refresh cost (and re-add additive price) for ${selectedIds.length} contract${selectedIds.length !== 1 ? 's' : ''} from the current inventory cost? Margins will recalculate against the existing prices.`,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
        try {
          const res = await fetch(`${API_URL}/api/pricing/contracts/refresh-costs`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ ids: selectedIds })
          });
          const data = await res.json();
          setSelectedIds([]);
          await loadContracts();
          const updated = data.updated?.length || 0;
          const skipped = data.skipped?.length || 0;
          const failed = data.failed?.length || 0;
          const parts = [`${updated} refreshed`];
          if (skipped > 0) parts.push(`${skipped} skipped (no inventory cost)`);
          if (failed > 0) parts.push(`${failed} failed`);
          setAlertDialog({ isOpen: true, message: parts.join(', '), type: failed > 0 ? 'warning' : 'success' });
        } catch (error) {
          console.error('Error bulk refreshing costs:', error);
          setAlertDialog({ isOpen: true, message: 'Failed to bulk refresh costs', type: 'error' });
        }
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === paginatedContracts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(paginatedContracts.map(c => c.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const dragSelect = useDragSelect({
    setChecked: (id, checked) => {
      setSelectedIds(prev => {
        if (checked) return prev.includes(id) ? prev : [...prev, id];
        return prev.filter(i => i !== id);
      });
    }
  });

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
      return next;
    });
  };

  // Expand-all / collapse-all toggle — see groupedRows just below.
  const toggleExpandAll = () => {
    const allGroupIds = groupedRows.filter(r => r.groupId).map(r => r.groupId);
    setExpandedGroups(prev =>
      allGroupIds.every(id => prev.has(id)) ? new Set() : new Set(allGroupIds)
    );
  };

  // OUR contract # chips — grey product-code-style so they visually
  // contrast with the supplier-contract chips (.contract-chip, primary).
  const renderContractNumberBadges = (val) => {
    const nums = String(val || '').split(/[,/]+/).map(s => s.trim()).filter(Boolean);
    if (nums.length === 0) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
        {nums.map((n, i) => (
          <span key={i} className='our-contract-chip' title='Our contract number (Excel Agri ↔ customer)'>{n}</span>
        ))}
      </div>
    );
  };

  // Collect distinct non-empty supplier contract numbers across a list
  // of contracts. Used to surface supplier refs on the group main row.
  const collectSupplierContractNumbers = (members) => {
    const out = [];
    const seen = new Set();
    for (const m of (members || [])) {
      const v = (m?.supplier_contract_number || '').trim();
      if (v && !seen.has(v)) { seen.add(v); out.push(v); }
    }
    return out;
  };

  // Group paginated contracts: contracts with same contract_group_id form a group
  const groupedRows = (() => {
    const rows = [];
    const seen = new Set();
    for (const c of paginatedContracts) {
      if (c.contract_group_id && !seen.has(c.contract_group_id)) {
        seen.add(c.contract_group_id);
        const members = paginatedContracts.filter(x => x.contract_group_id === c.contract_group_id);
        // A group of one is just a single row — don't bother with the group UI.
        if (members.length <= 1) {
          rows.push({ type: 'single', contract: c });
        } else {
          rows.push({ type: 'group', groupId: c.contract_group_id, contract: members[0], members });
        }
      } else if (!c.contract_group_id) {
        rows.push({ type: 'single', contract: c });
      }
    }
    return rows;
  })();

  // Right-click menus. Three row types render in this table with three
  // different action sets, so each gets its own hook instance. selectedIds is
  // an array here but the hook's selection contract is a read-only Set.
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const singleRows = groupedRows.filter(r => r.type === 'single').map(r => r.contract);
  const groupRowsForMenu = groupedRows.filter(r => r.type === 'group');
  const memberRows = groupRowsForMenu.flatMap(r => r.members);
  const parentOfMember = (m) =>
    groupRowsForMenu.find(r => r.groupId === m.contract_group_id)?.contract;

  const ctxContracts = useTableContextMenu({
    rows: singleRows,
    selectedIds: selectedIdSet,
    getRowId: (c) => c.id
  });
  const ctxGroups = useTableContextMenu({
    rows: groupRowsForMenu,
    getRowId: (r) => r.groupId
  });
  const ctxMembers = useTableContextMenu({
    rows: memberRows,
    selectedIds: selectedIdSet,
    getRowId: (m) => m.id
  });

  /**
   * Print every selected row as its own contract, in one PDF.
   *
   * Selection is by contract row id, but the printable unit is the GROUP —
   * so three members of the same group selected together produce one contract,
   * not three copies of it. Dedupe on the group id, preserving the order the
   * rows appear in the table so the printed stack matches the screen.
   */
  const handleBulkPrintContracts = () => {
    const selected = new Set(selectedIds);
    const seen = new Set();
    const rows = [];
    for (const c of filteredAndSortedContracts) {
      if (!selected.has(c.id)) continue;
      const key = c.contract_group_id ? `g:${c.contract_group_id}` : `c:${c.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push(c.contract_group_id ? { groupId: c.contract_group_id } : { contractId: c.id });
    }
    if (!rows.length) return;
    openContractPdf(rows, {
      onError: (message, type) => setAlertDialog({ isOpen: true, message, type: type || 'error' })
    });
  };

  // Mark / unmark fulfilled from the row menus. A grouped contract changes as a
  // whole on the server.
  // Make a draft contract live — its whole group, after the server has checked
  // every row's own dates against live contracts (Cam, 2026-10-22).
  const handleMakeLive = async (contract) => {
    try {
      const data = await makeContractLive(contract.id);
      await loadContracts();
      setAlertDialog({
        isOpen: true,
        type: 'success',
        message: data.made_live > 1 ? `${data.made_live} contract rows are now live.` : 'The contract is now live.'
      });
    } catch (e) {
      setAlertDialog({ isOpen: true, message: e.message, type: 'warning' });
    }
  };

  const handleSetFulfilled = async (ids, isFulfilled) => {
    try {
      await setContractsFulfilled(ids, isFulfilled);
      loadContracts();
    } catch (e) {
      setAlertDialog({ isOpen: true, message: `Failed to ${isFulfilled ? 'mark' : 'unmark'} the contract fulfilled: ${e.message}`, type: 'error' });
    }
  };

  const buildSingleActions = (contract) => [
    {
      key: 'contract',
      icon: 'description',
      label: 'Print contract',
      title: 'Two-page sales contract with terms overleaf — print double-sided',
      onClick: () => openContractPdf([{ contractId: contract.id }], {
        onError: (message, type) => setAlertDialog({ isOpen: true, message, type: type || 'error' })
      })
    },
    { key: 'view', icon: 'visibility', label: 'View', onClick: () => handleView(contract) },
    { key: 'orders', icon: 'playlist_add', label: 'Add orders…', title: 'The orders counted against this contract — add any of this customer\'s orders by hand, or take one off', onClick: () => setOrdersFor({ set: { kind: 'contract', ids: [contract.id] }, title: `${contract.company_name} — ${contract.product_name || 'contract'}` }) },
    { key: 'notes', icon: 'sticky_note_2', label: contract.notes ? 'View Notes' : 'No Notes', disabled: !contract.notes, onClick: () => setNotesDialog({ isOpen: true, notes: contract.notes, title: `${contract.company_name} - ${contract.product_name}` }) },
    { key: 'hold', icon: contract.is_on_hold ? 'play_circle' : 'pause_circle', label: contract.is_on_hold ? 'Resume' : 'Hold', className: contract.is_on_hold ? 'action-unhold' : 'action-hold', onClick: () => handleToggleHold(contract) },
    fulfilledAction({ fulfilled: !!contract.is_fulfilled_override, onToggle: () => handleSetFulfilled([contract.id], !contract.is_fulfilled_override) }),
    { key: 'edit', icon: 'edit', label: 'Edit', onClick: () => handleEdit(contract) },
    makeLiveAction({ draft: !!contract.is_draft, onClick: () => handleMakeLive(contract) }),
    { key: 'refresh', icon: 'sync', label: 'Refresh cost', onClick: () => handleRefreshCost(contract) },
    { key: 'archive', icon: contract.is_archived ? 'unarchive' : 'archive', label: contract.is_archived ? 'Unarchive' : 'Archive', onClick: () => handleArchive(contract) },
  ];

  const buildGroupActions = ({ contract, members }) => [
    {
      key: 'contract',
      icon: 'description',
      label: 'Print contract',
      title: 'Two-page sales contract with terms overleaf — print double-sided',
      onClick: () => openContractPdf([{ groupId: contract.contract_group_id }], {
        onError: (message, type) => setAlertDialog({ isOpen: true, message, type: type || 'error' })
      })
    },
    { key: 'view', icon: 'visibility', label: 'View group', onClick: () => handleView(contract) },
    { key: 'orders', icon: 'playlist_add', label: 'Add orders…', title: 'The orders counted against this contract — add any of this customer\'s orders by hand, or take one off', onClick: () => setOrdersFor({ set: { kind: 'contract', ids: members.map(x => x.id) }, title: `${contract.company_name} — product group` }) },
    { key: 'notes', icon: 'sticky_note_2', label: contract.notes ? 'View Notes' : 'No Notes', disabled: !contract.notes, onClick: () => setNotesDialog({ isOpen: true, notes: contract.notes, title: `${contract.company_name} — Product Group` }) },
    {
      key: 'hold',
      icon: members.every(m => m.is_on_hold) ? 'play_circle' : 'pause_circle',
      label: members.every(m => m.is_on_hold) ? 'Resume group' : 'Hold group',
      className: members.every(m => m.is_on_hold) ? 'action-unhold' : 'action-hold',
      onClick: async () => {
        const target = !members.every(m => m.is_on_hold);
        await fetch(`${API_URL}/api/pricing/contracts/bulk-hold`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ ids: members.map(m => m.id), on_hold: target })
        });
        loadContracts();
      }
    },
    fulfilledAction({
      subject: 'contract group',
      fulfilled: members.length > 0 && members.every(m => m.is_fulfilled_override),
      onToggle: () => handleSetFulfilled(members.map(m => m.id), !members.every(m => m.is_fulfilled_override))
    }),
    { key: 'edit', icon: 'edit', label: 'Edit group', onClick: () => handleEdit(contract) },
    makeLiveAction({ subject: 'contract group', draft: members.some(m => m.is_draft), onClick: () => handleMakeLive(contract) }),
    { key: 'refresh', icon: 'sync', label: 'Refresh cost', disabled: true, title: 'Refresh cost from inventory — use the per-row button on each contract in this group' },
    {
      key: 'archive',
      icon: members.every(m => m.is_archived) ? 'unarchive' : 'archive',
      label: members.every(m => m.is_archived) ? 'Unarchive group' : 'Archive group',
      onClick: async () => {
        const target = !members.every(m => m.is_archived);
        await Promise.all(members.map(m =>
          fetch(`${API_URL}/api/pricing/contracts/${m.id}/archive`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ is_archived: target })
          })
        ));
        loadContracts();
      }
    },
  ];

  const buildMemberActions = (m, contract) => [
    {
      key: 'contract',
      icon: 'description',
      label: 'Print contract',
      title: 'Prints the whole group this product belongs to — a single line is not a contract on its own',
      onClick: () => openContractPdf([{ groupId: contract.contract_group_id }], {
        onError: (message, type) => setAlertDialog({ isOpen: true, message, type: type || 'error' })
      })
    },
    { key: 'view', icon: 'visibility', label: 'View group', onClick: () => handleView(contract) },
    { key: 'orders', icon: 'playlist_add', label: 'Add orders…', title: 'The orders counted against this contract — add any of this customer\'s orders by hand, or take one off', onClick: () => setOrdersFor({ set: { kind: 'contract', ids: [m.id] }, title: `${m.company_name} — ${m.product_name || 'contract'}` }) },
    { key: 'notes', icon: 'sticky_note_2', label: m.notes ? 'View Notes' : 'No Notes', disabled: !m.notes, onClick: () => setNotesDialog({ isOpen: true, notes: m.notes, title: `${m.company_name} - ${m.product_name}` }) },
    { key: 'hold', icon: m.is_on_hold ? 'play_circle' : 'pause_circle', label: m.is_on_hold ? 'Resume' : 'Hold', className: m.is_on_hold ? 'action-unhold' : 'action-hold', onClick: () => handleToggleHold(m) },
    fulfilledAction({ subject: 'contract group', fulfilled: !!m.is_fulfilled_override, onToggle: () => handleSetFulfilled([m.id], !m.is_fulfilled_override) }),
    { key: 'edit', icon: 'edit', label: 'Edit group', onClick: () => handleEdit(contract) },
    makeLiveAction({ subject: 'contract group', draft: !!m.is_draft, onClick: () => handleMakeLive(m) }),
    { key: 'refresh', icon: 'sync', label: 'Refresh cost', onClick: () => handleRefreshCost(m) },
    { key: 'archive', icon: 'archive', label: 'Archive', disabled: true, title: 'Archive whole group instead' },
  ];

  const buildBulkActions = (ids) => [
    { key: 'bulk-hold', icon: 'pause_circle', label: `Hold ${ids.size} contracts`, onClick: () => handleBulkHold(true) },
    { key: 'bulk-resume', icon: 'play_circle', label: `Resume ${ids.size} contracts`, onClick: () => handleBulkHold(false) },
    { key: 'bulk-refresh', icon: 'sync', label: `Refresh costs on ${ids.size} contracts`, onClick: handleBulkRefreshCost },
  ];

  const buildGenericActions = () => {
    const allExpanded = groupRowsForMenu.length > 0 && groupRowsForMenu.every(r => expandedGroups.has(r.groupId));
    const allSelected = paginatedContracts.length > 0 && selectedIds.length === paginatedContracts.length;
    return [
      {
        key: 'select-all',
        icon: allSelected ? 'deselect' : 'select_all',
        label: allSelected ? 'Clear selection' : 'Select all on this page',
        disabled: paginatedContracts.length === 0,
        onClick: toggleSelectAll
      },
      groupRowsForMenu.length > 0 && {
        key: 'expand-all',
        icon: allExpanded ? 'unfold_less' : 'unfold_more',
        label: allExpanded ? 'Collapse all groups' : 'Expand all groups',
        onClick: toggleExpandAll
      },
      {
        key: 'show-archived',
        icon: 'inventory_2',
        label: 'Show archived',
        active: !!filters.showArchived,
        onClick: () => handleFilterChange('showArchived', !filters.showArchived)
      },
      { key: 'clear-filters', icon: 'filter_alt_off', label: 'Clear filters', onClick: handleClearFilters },
      { key: 'refresh-all', icon: 'refresh', label: 'Refresh contracts', onClick: () => loadContracts() }
    ];
  };

  return (
    <div className="contract-pricing compounds-pricing">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">
            Manage seasonal compounds contract pricing
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={handleClearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <RecalculateQuantitiesButton
              user={user}
              onDone={() => loadContracts()}
              onAlert={setAlertDialog}
            />
            <RefreshAutoLinksButton
              user={user}
              periods={periods}
              onDone={() => loadContracts()}
              onAlert={setAlertDialog}
            />
            <div>
              <button className="btn-slideout" onClick={handleAddNew}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">add</span>
                </div>
                <span className="btn-label">Add New</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters" style={{ paddingBottom: 'var(--spacing-md' }}>
        <div className="filter-row" style={{ gridTemplateColumns: '1fr 150px 135px 195px 150px 60px 85px 70px' }}>
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['customer', 'account no.', 'product', 'product code', 'contract no. (ours and the supplier\'s)', 'or the fields chosen in Search In']} />
            </label>
            <Input
              type="text"
              placeholder='Terms, or "exact phrase"…'
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              icon="search"
            />
          </div>

          <div className="filter-group">
            <label>Search In</label>
            <MultiSelectDropdown
              options={SEARCH_FIELD_OPTIONS}
              value={searchFields}
              onChange={setSearchFields}
              summarize={searchFieldsSummary}
              hideTags
              placeholder="Search All"
            />
          </div>

          <div className="filter-group">
            <label>Contract Period</label>
            <Dropdown
              value={filters.period}
              onChange={(value) => handleFilterChange('period', value)}
              options={[
                { value: '', label: 'All Periods' },
                ...periods.map(p => ({
                  value: p.id.toString(),
                  label: p.name
                }))
              ]}
              placeholder="All Periods"
            />
          </div>

          <div className="filter-group">
            <label>Mill</label>
            <Dropdown
              value={filters.mill}
              onChange={(value) => handleFilterChange('mill', value)}
              options={[
                { value: '', label: 'All Suppliers' },
                ...mills.map(supplier => ({
                  value: supplier.id.toString(),
                  label: supplier.company_name
                }))
              ]}
            />
          </div>

          <div className="filter-group">
            <label>Rep</label>
            <Dropdown
              value={filters.rep}
              onChange={(value) => handleFilterChange('rep', value)}
              options={[
                { value: '', label: 'All Reps' },
                ...reps.map(rep => ({
                  value: rep,
                  label: rep
                }))
              ]}
            />
          </div>

          <div className="filter-group">
            <label>Archived</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="show-archived-compounds"
                  type="checkbox"
                  checked={!!filters.showArchived}
                  onChange={(e) => handleFilterChange('showArchived', e.target.checked)}
                />
                <label htmlFor="show-archived-compounds"></label>
              </div>
            </div>
          </div>

          <div className="filter-group">
            <label title="Untick to hide rows / groups that already have a contract number attached (ours or supplier's).">Contracted</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="show-contracted-compounds"
                  type="checkbox"
                  checked={!!filters.showContracted}
                  onChange={(e) => handleFilterChange('showContracted', e.target.checked)}
                />
                <label htmlFor="show-contracted-compounds"></label>
              </div>
            </div>
          </div>

          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown
              value={filters.limit}
              onChange={(value) => handleFilterChange('limit', value)}
              options={[
                { value: '20', label: '20' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
                { value: '200', label: '200' }
              ]}
              placeholder="20"
            />
          </div>

        </div>
      </div>

      <div className="table-toolbar">
        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
          {(() => {
            const groupRows = groupedRows.filter(r => r.groupId);
            if (groupRows.length === 0) return null;
            const allExpanded = groupRows.every(r => expandedGroups.has(r.groupId));
            return (
              <button
                className="btn-secondary btn-sm"
                onClick={toggleExpandAll}
                disabled={loading}
              >
                <span className="material-symbols-rounded">{allExpanded ? 'unfold_less' : 'unfold_more'}</span>
                {allExpanded ? 'Collapse All' : 'Expand All'}
              </button>
            );
          })()}
          <ResetButton />
        </div>
        {pagination.totalPages > 0 && (
          <PaginationControls
            pagination={pagination}
            onPageChange={changePage}
          />
        )}
      </div>

      {selectedIds.length > 0 && (
        <div className="bulk-actions-bar">
          <div className="bulk-actions-info">
            <label className="tick-check-label" style={{ gap: '0' }}>
              <input type="checkbox" className="tick-check-input" id="bulk-compound-indicator" checked readOnly />
              <label htmlFor="bulk-compound-indicator" className="tick-check">
                <svg width="16px" height="16px" viewBox="0 0 18 18">
                  <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                  <polyline points="1 9 7 14 15 4"></polyline>
                </svg>
              </label>
            </label>
            <span>{selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected</span>
          </div>
          <div className="bulk-actions-buttons slideout">
            <div>
              <button className="btn-slideout" onClick={handleBulkPrintContracts}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">description</span></div>
                <span className="btn-label">Print Contracts</span>
              </button>
            </div>
            <div>
              <button className="btn-round btn-hold" onClick={() => handleBulkHold(true)}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">pause_circle</span></div>
                <span className="btn-label">Hold</span>
              </button>
            </div>
            <div>
              <button className="btn-round btn-unhold" onClick={() => handleBulkHold(false)}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">play_circle</span></div>
                <span className="btn-label">Resume</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={handleBulkRefreshCost}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">sync</span></div>
                <span className="btn-label">Refresh Costs</span>
              </button>
            </div>
            <div>
              <button data-read-safe className="btn-round btn-danger" onClick={() => setSelectedIds([])}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                <span className="btn-label">Clear</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="table-container">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <table className="data-table resizable-table" ref={tableRef} onContextMenu={ctxContracts.onTableContextMenu}>
            <ColGroup />
            <thead>
              <tr>
                <th className="checkbox-col">
                  <label className="tick-check-label" style={{ justifyContent: 'center', gap: '0' }}>
                    <input type="checkbox" className="tick-check-input" id="select-all-compounds" checked={paginatedContracts.length > 0 && selectedIds.length === paginatedContracts.length} onChange={toggleSelectAll} />
                    <label htmlFor="select-all-compounds" className="tick-check">
                      <svg width="16px" height="16px" viewBox="0 0 18 18"><path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path><polyline points="1 9 7 14 15 4"></polyline></svg>
                    </label>
                  </label>
                  <ResizeHandle colIndex={0} />
                </th>
                <th className="expand-cell"><ResizeHandle colIndex={1} /></th>
                <th className="sortable" onClick={() => handleSort('company_name')}>
                  Customer {sorting.field === 'company_name' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={2} />
                </th>
                <th className="sortable" onClick={() => handleSort('product_name')}>
                  Product {sorting.field === 'product_name' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={3} />
                </th>
                <th className="sortable" onClick={() => handleSort('contract_number')}>
                  Contract {sorting.field === 'contract_number' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={4} />
                </th>
                <th style={{ textAlign: 'center' }} className="sortable" onClick={() => handleSort('period_names')}>
                  Period(s) {sorting.field === 'period_names' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={5} />
                </th>
                <th className="sortable" onClick={() => handleSort('start_date')}>
                  Dates {sorting.field === 'start_date' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={6} />
                </th>
                <th className="sortable" onClick={() => handleSort('mill')}>
                  Supplier {sorting.field === 'mill' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={7} />
                </th>
                <th className="text-right sortable" onClick={() => handleSort('quantity')}>
                  Quant {sorting.field === 'quantity' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={8} />
                </th>
                <th className="text-right sortable" onClick={() => handleSort('margin')}>
                  Price / Margin {sorting.field === 'margin' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={9} />
                </th>
                <th className="sortable" onClick={() => handleSort('rep_name')}>
                  Rep {sorting.field === 'rep_name' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={10} />
                </th>
                <th className="actions-col"><ResizeHandle colIndex={11} /></th>
              </tr>
            </thead>
            <tbody>
              {groupedRows.length === 0 ? (
                <tr>
                  <td colSpan="13" className="text-center">No contracts found</td>
                </tr>
              ) : (
                groupedRows.map((row) => {
                  if (row.type === 'single') {
                    const contract = row.contract;
                    return (
                      <tr key={contract.id} className={contract.is_archived ? 'archived' : ''} style={{ cursor: 'pointer', ...(contract.is_on_hold ? { opacity: 0.6 } : {}) }} onClick={() => handleEdit(contract)} onContextMenu={ctxContracts.onRowContextMenu(contract)}>
                        <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                          <label
                            className="tick-check-label"
                            style={{ justifyContent: 'center', gap: '0' }}
                            onMouseDown={dragSelect.onMouseDown(contract.id, selectedIds.includes(contract.id))}
                            onMouseEnter={dragSelect.onMouseEnter(contract.id)}
                            onClick={(e) => e.preventDefault()}
                          >
                            <input type="checkbox" className="tick-check-input" id={`select-compound-${contract.id}`} checked={selectedIds.includes(contract.id)} onChange={() => toggleSelect(contract.id)} />
                            <label htmlFor={`select-compound-${contract.id}`} className="tick-check">
                              <svg width="16px" height="16px" viewBox="0 0 18 18"><path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path><polyline points="1 9 7 14 15 4"></polyline></svg>
                            </label>
                          </label>
                        </td>
                        <td className="expand-cell" />
                        <td>
                          <ContractCustomerCell
                            accountNo={contract.account_no}
                            accountId={contract.account_id}
                            onAccountClick={() => setSelectedContactId(contract.account_id)}
                            name={contract.company_name}
                            onNameClick={() => { if (contract.account_id) setSelectedContactId(contract.account_id); }}
                            flags={{ hold: contract.is_on_hold, draft: contract.is_draft, archived: contract.is_archived }}
                            status={(() => {
                              const c = parseFloat(contract.quantity || 0);
                              const u = parseFloat(contract.ordered_quantity || 0);
                              return !contract.is_fulfilled_override && c > 0 && (c - u) < 1 ? 'FULFILLED' : null;
                            })()}
                            manualFulfilled={contract.is_fulfilled_override}
                            linkedPoCount={contract.linked_po_count}
                            onLinkedClick={() => handleView(contract)}
                            address={contract.address_line}
                            scopeKind={contract.scope_kind}
                          />
                        </td>
                        <td>
                          <ProductCell
                            productCode={contract.product_code}
                            additiveCode={contract.additive_code}
                            productName={contract.product_name}
                            isHolding={contract.is_holding_product}
                            additiveName={contract.additive_name}
                          />
                          <ContractGroupBadge group={contract.product_group} />
                        </td>
                        <td>{renderContractNumberBadges(contract.contract_number)}</td>
                        <td style={{ textAlign: 'center' }}>
                          {(contract.period_names || contract.period_name || '').split(', ').filter(Boolean).map((p, i) => (
                            <React.Fragment key={i}>
                              <span className={`period-badge ${p.toLowerCase().startsWith('winter') ? 'winter' : 'summer'}${isPastPeriodName(periods, p) ? ' past-period' : ''}`}>{p}</span>
                              {isCurrentPeriodName(p) && contractCoversToday(contract) && (
                                <span className="material-symbols-rounded period-current-icon" title="Current — this contract’s dates cover today">check_circle</span>
                              )}
                            </React.Fragment>
                          ))}
                        </td>
                        <td>
                          <div>{contract.start_date ? new Date(contract.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-'}</div>
                          <div>{contract.end_date ? new Date(contract.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-'}</div>
                        </td>
                        <td>{contract.supplier_name || contract.mill || '-'}</td>
                        <td className="text-right">{contract.quantity ? formatQuantity(contract.quantity) : '-'}</td>
                        <td className="text-right">
                          <PriceMarginCell price={contract.price} cost={contract.cost} margin={contract.margin} />
                        </td>
                        <td>{contract.rep_name || '-'}</td>
                        <td className="actions-col" style={{ textAlign: "center", verticalAlign: "middle" }} onClick={(e) => e.stopPropagation()}>
                          <RowActionsMenu actions={buildSingleActions(contract)} />
                        </td>
                      </tr>
                    );
                  }

                  // Group row
                  const { groupId, contract, members } = row;
                  const isExpanded = expandedGroups.has(groupId);
                  const groupArchived = members.length > 0 && members.every(m => m.is_archived);
                  // Whole group is on hold when every member is held (a group-level
                  // hold sets all members) — surface it on the parent like a single row.
                  const groupHeld = members.length > 0 && members.every(m => m.is_on_hold);
                  return (
                    <React.Fragment key={`group-${groupId}`}>
                      <tr className={`expandable-row ${isExpanded ? 'expanded' : ''} ${groupArchived ? 'archived' : ''} ${groupHeld ? 'on-hold' : ''}`} onClick={() => toggleGroup(groupId)} onContextMenu={ctxGroups.onRowContextMenu(row)}>
                        <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                          <label className="tick-check-label" style={{ justifyContent: 'center', gap: '0' }}>
                            <input type="checkbox" className="tick-check-input" id={`select-group-${groupId}`}
                              checked={members.every(m => selectedIds.includes(m.id))}
                              onChange={() => {
                                const allSelected = members.every(m => selectedIds.includes(m.id));
                                setSelectedIds(prev => allSelected ? prev.filter(id => !members.some(m => m.id === id)) : [...new Set([...prev, ...members.map(m => m.id)])]);
                              }} />
                            <label htmlFor={`select-group-${groupId}`} className="tick-check">
                              <svg width="16px" height="16px" viewBox="0 0 18 18"><path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path><polyline points="1 9 7 14 15 4"></polyline></svg>
                            </label>
                          </label>
                        </td>
                        <td className="expand-cell">
                          <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▼</span>
                        </td>
                        <td>
                          <ContractCustomerCell
                            accountNo={contract.account_no}
                            accountId={contract.account_id}
                            onAccountClick={() => setSelectedContactId(contract.account_id)}
                            name={contract.company_name}
                            onNameClick={() => { if (contract.account_id) setSelectedContactId(contract.account_id); }}
                            flags={{ archived: groupArchived, hold: groupHeld, draft: members.length > 0 && members.every(m => m.is_draft) }}
                            linkedPoCount={members.reduce((s, m) => s + (parseInt(m.linked_po_count) || 0), 0)}
                            onLinkedClick={() => handleView(contract)}
                            linkedScope="group total"
                            address={contract.address_line}
                            scopeKind={contract.scope_kind}
                          />
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            {/* Made from a product group (29 Oct 2026): its name, and
                                what the group has gained since. */}
                            {contract.product_group
                              ? <ContractGroupBadge group={contract.product_group} />
                              : <span style={{ fontWeight: 500 }}>Product Group</span>}
                            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-xs)' }}>({members.length} items)</span>
                          </div>
                        </td>
                        <td>{renderContractNumberBadges(contract.contract_number)}</td>
                        <td style={{ textAlign: 'center' }}>
                          {(contract.period_names || contract.period_name || '').split(', ').filter(Boolean).map((p, i) => (
                            <React.Fragment key={i}>
                              <span className={`period-badge ${p.toLowerCase().startsWith('winter') ? 'winter' : 'summer'}${isPastPeriodName(periods, p) ? ' past-period' : ''}`}>{p}</span>
                              {isCurrentPeriodName(p) && contractCoversToday(contract) && (
                                <span className="material-symbols-rounded period-current-icon" title="Current — this contract’s dates cover today">check_circle</span>
                              )}
                            </React.Fragment>
                          ))}
                        </td>
                        <td>
                          <div>{contract.start_date ? new Date(contract.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-'}</div>
                          <div>{contract.end_date ? new Date(contract.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-'}</div>
                        </td>
                        <td style={{ color: 'var(--text-muted)' }}>—</td>
                        <td className="text-right">
                          <div>{contract.quantity ? formatQuantity(contract.quantity) : '-'}</div>
                          {contract.quantity > 0 && (() => {
                            const contracted = parseFloat(contract.quantity);
                            const used = members.reduce((acc, mm) => acc + parseFloat(mm.ordered_quantity || 0), 0);
                            const remaining = contracted - used;
                            return (
                              <div style={{
                                fontSize: 'var(--font-xs)',
                                color: remaining < 0 ? 'var(--color-error)' : remaining === 0 ? 'var(--text-secondary)' : 'var(--text-muted)',
                                fontWeight: remaining < 0 ? '600' : 'normal'
                              }}>
                                ({formatQuantity(remaining)})
                              </div>
                            );
                          })()}
                        </td>
                        <td className="text-right"></td>
                        <td>{contract.rep_name || '-'}</td>
                        <td className="actions-col" style={{ textAlign: "center", verticalAlign: "middle" }} onClick={(e) => e.stopPropagation()}>
                          <RowActionsMenu actions={buildGroupActions(row)} />
                        </td>
                      </tr>
                      {isExpanded && members.map((m) => (
                        <tr
                          key={`member-${m.id}`}
                          className={`sub-row ${m.is_on_hold ? 'on-hold' : ''} ${m.is_archived ? 'archived' : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleEdit(contract)}
                          onContextMenu={ctxMembers.onRowContextMenu(m)}
                        >
                          <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                            <label
                              className="tick-check-label"
                              style={{ justifyContent: 'center', gap: '0' }}
                              onMouseDown={dragSelect.onMouseDown(m.id, selectedIds.includes(m.id))}
                              onMouseEnter={dragSelect.onMouseEnter(m.id)}
                              onClick={(e) => e.preventDefault()}
                            >
                              <input type="checkbox" className="tick-check-input" id={`select-compound-sub-${m.id}`} checked={selectedIds.includes(m.id)} onChange={() => toggleSelect(m.id)} />
                              <label htmlFor={`select-compound-sub-${m.id}`} className="tick-check">
                                <svg width="16px" height="16px" viewBox="0 0 18 18"><path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path><polyline points="1 9 7 14 15 4"></polyline></svg>
                              </label>
                            </label>
                          </td>
                          <td className="expand-cell" />
                          <td />
                          <td>
                            <div style={{ display: 'flex', gap: '3px', flexDirection: 'column' }}>
                              <ProductCell
                                productCode={m.product_code}
                                additiveCode={m.additive_code}
                                productName={m.product_name}
                                isHolding={m.is_holding_product}
                                additiveName={m.additive_name}
                              />
                              <span className="sub-row-badges">
                                {m.is_on_hold && <span className="hold-badge">HOLD</span>}
                                {m.is_draft && <span className="draft-badge">DRAFT</span>}
                                {m.is_archived && <span className="archived-badge">ARCHIVED</span>}
                                {(() => {
                                  const c = parseFloat(m.quantity || 0);
                                  const u = parseFloat(m.ordered_quantity || 0);
                                  const fulfilled = !m.is_fulfilled_override && c > 0 && (c - u) < 1;
                                  return <FulfilmentBadge status={fulfilled ? 'FULFILLED' : null} manual={m.is_fulfilled_override} />;
                                })()}
                                <LinkedPosBadge count={m.linked_po_count} onClick={() => handleView(contract)} />
                              </span>
                            </div>
                          </td>
                          <td>{m.supplier_contract_number
                            ? <span className="contract-chip" title="Supplier contract number">{m.supplier_contract_number}</span>
                            : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                          <td />
                          <td />
                          <td>{m.supplier_name || m.mill || '-'}</td>
                          <td className="text-right">
                            <div style={{ fontSize: 'var(--font-xs)', color: 'var(--text-muted)' }}>
                              ({formatQuantity(m.ordered_quantity || 0)} used)
                            </div>
                          </td>
                          <td className="text-right">
                            <PriceMarginCell price={m.price} cost={m.cost} margin={m.margin} />
                          </td>
                          <td />
                          <td className="actions-col" style={{ textAlign: "center", verticalAlign: "middle" }} onClick={(e) => e.stopPropagation()}>
                            <RowActionsMenu actions={buildMemberActions(m, contract)} />
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <ContractPricingModal
          contract={selectedContract}
          categoryId={CATEGORY_ID}
          mode={modalMode}
          onClose={() => { setShowModal(false); loadContracts(); }}
          onSave={handleSave}
        />
      )}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />

      <AlertDialog
        isOpen={notesDialog.isOpen}
        title={notesDialog.title}
        message={notesDialog.notes}
        type="info"
        onClose={() => setNotesDialog({ isOpen: false, notes: '', title: '' })}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      {selectedContactId && ReactDOM.createPortal(
        <ContactModal
          contactId={selectedContactId}
          onClose={() => setSelectedContactId(null)}
        />,
        document.body
      )}

      {/* Table context menus (right-click) — one per row type */}
      {ordersFor && (
        <ContractOrdersModal
          set={ordersFor.set}
          title={ordersFor.title}
          onClose={() => { setOrdersFor(null); loadContracts(); }}
        />
      )}

      <TableContextMenu
        ctx={ctxContracts}
        rowActions={buildSingleActions}
        bulkActions={buildBulkActions}
        actions={buildGenericActions}
      />
      <TableContextMenu
        ctx={ctxGroups}
        rowActions={buildGroupActions}
        actions={buildGenericActions}
      />
      <TableContextMenu
        ctx={ctxMembers}
        rowActions={(m) => buildMemberActions(m, parentOfMember(m))}
        bulkActions={buildBulkActions}
        actions={buildGenericActions}
      />
    </div>
  );
}

export default CompoundPricing;
